import React from 'react';

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-900 via-violet-900 to-fuchsia-900">
      {/* 背景の抽象的なグラフィック */}
      <div className="absolute inset-0">
        <img 
          src="https://readdy.ai/api/search-image?query=Abstract%20elegant%20purple%20violet%20gradient%20flowing%20waves%20digital%20art%20modern%20minimalist%20design%20sophisticated%20geometric%20patterns%20futuristic%20technology%20background%20smooth%20curves%20professional%20atmosphere%20no%20people&width=1920&height=1080&seq=hero-abstract-waves-3001&orientation=landscape"
          alt="背景"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-violet-900/70 to-fuchsia-900/80"></div>
      </div>

      {/* 装飾的な図形 - より独創的なデザイン */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-10 right-10 w-[500px] h-[500px] bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 left-10 w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/4 right-1/4 w-72 h-72 border-2 border-fuchsia-400/40 rounded-full" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}></div>
        <div className="absolute bottom-1/3 left-1/3 w-56 h-56 border-2 border-violet-400/40" style={{ transform: 'rotate(30deg)', clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 border border-purple-300/20 rounded-full" style={{ transform: 'translate(-50%, -50%)' }}></div>
      </div>

      {/* メインコンテンツ */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* 左側：テキストコンテンツ */}
          <div className="text-left">
            <div className="mb-8">
              <span className="inline-block px-8 py-3 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-sm font-black tracking-widest rounded-full shadow-2xl border-2 border-white/20" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                WEB CONTENT CREATION
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-tight" style={{ fontFamily: '"Shippori Mincho", serif', textShadow: '4px 4px 12px rgba(0,0,0,0.6)' }}>
              デジタルで紡ぐ、<br />
              <span className="bg-gradient-to-r from-fuchsia-300 to-violet-300 bg-clip-text text-transparent">新しい物語</span>
            </h1>

            <p className="text-xl md:text-2xl text-purple-100 mb-12 leading-relaxed font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif', textShadow: '2px 2px 6px rgba(0,0,0,0.5)' }}>
              企画から制作、運営まで。<br />
              あなたのビジネスを次のステージへ導く、<br />
              ウェブコンテンツのトータルソリューション
            </p>

            <div className="flex flex-col sm:flex-row gap-6 mb-16">
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-12 py-5 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white rounded-full font-black text-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl active:scale-95 shadow-xl border-2 border-white/30 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
              >
                お問い合わせ
              </a>
              <a 
                href="#services" 
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-12 py-5 bg-white/10 text-white rounded-full font-black text-lg transition-all duration-300 hover:scale-110 hover:bg-white/20 hover:shadow-2xl active:scale-95 shadow-xl border-2 border-white/30 backdrop-blur-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
              >
                サービスを見る
              </a>
            </div>

            {/* 特徴アイコン - 横並び */}
            <div className="flex flex-wrap gap-6">
              {[
                { icon: 'ri-lightbulb-flash-line', label: '企画力' },
                { icon: 'ri-palette-line', label: '制作力' },
                { icon: 'ri-settings-3-line', label: '運営力' },
                { icon: 'ri-line-chart-line', label: '成長力' }
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-fuchsia-400 to-violet-500 rounded-xl flex items-center justify-center border-2 border-white/30 shadow-lg">
                    <i className={`${item.icon} text-2xl text-white w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <span className="text-white font-black text-base" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 右側：装飾的なグラフィック要素 */}
          <div className="relative hidden lg:block">
            <div className="relative w-full h-[600px]">
              {/* 中央の大きな円 */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-fuchsia-500/30 to-violet-500/30 rounded-full backdrop-blur-xl border-2 border-white/20 shadow-2xl flex items-center justify-center">
                <div className="w-80 h-80 bg-gradient-to-br from-purple-600/40 to-fuchsia-600/40 rounded-full backdrop-blur-lg border border-white/10 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-sparkling-2-fill text-8xl text-white mb-4 w-20 h-20 flex items-center justify-center mx-auto"></i>
                    <p className="text-white text-2xl font-black" style={{ fontFamily: '"Shippori Mincho", serif' }}>
                      創造する力
                    </p>
                  </div>
                </div>
              </div>
              
              {/* 周囲の小さな円 */}
              <div className="absolute top-10 right-20 w-32 h-32 bg-gradient-to-br from-fuchsia-400/50 to-violet-400/50 rounded-full backdrop-blur-lg border border-white/20 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
              <div className="absolute bottom-20 right-10 w-24 h-24 bg-gradient-to-br from-violet-400/50 to-purple-400/50 rounded-full backdrop-blur-lg border border-white/20 shadow-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
              <div className="absolute top-32 left-10 w-28 h-28 bg-gradient-to-br from-purple-400/50 to-fuchsia-400/50 rounded-full backdrop-blur-lg border border-white/20 shadow-xl animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* 波形の装飾 - より独創的なデザイン */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="#F3E8FF" fillOpacity="0.5"/>
          <path d="M0 120L48 105C96 90 192 60 288 50C384 40 480 50 576 60C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="#F3E8FF"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
